import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Layers, Send, Trash2, FileWarning } from "lucide-react"
import { REGION_OPTIONS, URGENCY_OPTIONS } from "./constants"
import { useCreateGroupOrderMutation } from "shared/redux/endpoints/procurementApprovals"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import {
  Page,
  Card,
  Button,
  Currency,
  StatusBadge,
  DataTable,
  EmptyState,
  Alert,
  Input,
  Select,
} from "./ui"
import { getApiErrorMessage } from "./orderSubmitFeedback"
import OrderSubmitConfirmModal from "./components/orders/OrderSubmitConfirmModal"
import { ORDER_STATUS } from "./status"
import { RULES, fieldError } from "./fieldRules"

// Responsive field grid — collapses to one column on mobile.
const FieldGrid = ({ children, min = 220 }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: 16,
    }}
  >
    {children}
  </div>
)

// Bundles several of the requester's own order requests — picked via the
// checkboxes on the Order Request list — under one group header. Unlike a
// single order request there's no draft state: POST /group-orders creates it
// already `pending` (auto-submitted), so this is a single create action, not
// a create-then-submit flow.
const GroupOrderFormContainer = () => {
  const navigate = useNavigate()
  const { state } = useLocation()

  // Backend rule: every order in orderRequestIds must be Pending or Approved
  // — a group is formed from orders that have either already cleared into
  // the approval queue or already been decided, not drafts still being
  // edited. The Orders page checkbox that feeds `state.orders` already only
  // lets Pending/Approved rows be selected, but this filters defensively
  // (e.g. a row's status changed between selecting it and landing here) and
  // tells the user what got dropped instead of silently submitting a
  // request BE will 400 anyway.
  const isGroupable = (r) => r.status === ORDER_STATUS.PENDING || r.status === ORDER_STATUS.APPROVED
  const incomingOrders = state?.orders || []
  const skippedOrders = incomingOrders.filter((r) => !isGroupable(r))
  const [items, setItems] = useState(() => incomingOrders.filter(isGroupable))
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [form, setForm] = useState({ title: "", region: "", urgency: "normal" })
  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }))

  const [createGroupOrder, { isLoading: busy }] = useCreateGroupOrderMutation()

  useEffect(() => {
    if (skippedOrders.length) {
      showToastError(
        `${skippedOrders.length} order(s) skipped — only orders Waiting for approval or Approved can be grouped.`
      )
    }
  }, [])

  const removeItem = (id) => setItems((prev) => prev.filter((r) => r.id !== id))

  const totalAmount = useMemo(
    () => items.reduce((sum, r) => sum + (Number(r.total) || 0), 0),
    [items]
  )

  const columns = useMemo(
    () => [
      { key: "orCode", header: "OR Code", strong: true },
      { key: "title", header: "Title" },
      { key: "categoryName", header: "Category" },
      {
        key: "total",
        header: "Total",
        align: "right",
        render: (r) => <Currency value={r.total} />,
      },
      {
        key: "status",
        header: "Status",
        render: (r) => <StatusBadge status={r.status} />,
      },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 aria-hidden />}
            onClick={() => removeItem(r.id)}
            style={{ color: "var(--pc-danger)" }}
          >
            Remove
          </Button>
        ),
      },
    ],
    []
  )

  const buildPayload = () => ({
    title: form.title.trim(),
    region: form.region || undefined,
    urgency: form.urgency ? form.urgency.toUpperCase() : undefined,
    orderRequestIds: items.map((r) => r.id),
  })

  // Single create call — the group order is `pending` the moment it exists.
  // createGroupOrder invalidates the requester's order list tag, so the
  // Order Request page picks it up as a real row (type "group") on its own
  // next fetch — no need to hand the created record over by hand.
  const handleCreate = async () => {
    try {
      const created = await createGroupOrder(buildPayload()).unwrap()
      showToastSuccess(`Group order ${created?.code || ""} created.`.trim())
      navigate("/procurement/order-requests")
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this group order. Please try again."))
      setConfirmOpen(false)
    }
  }

  const canSubmit = !busy && !!form.title.trim() && !!form.region && items.length > 0
  const [showTitleError, setShowTitleError] = useState(false)
  const titleError = fieldError(form.title, RULES.groupOrder.title)

  return (
    <Page
      breadcrumb={["Procurement", "Order Request", "Group Order"]}
      title="New Group Order"
      subtitle="Bundle several order requests together — creating it submits the group for approval right away."
      actions={
        <Button variant="secondary" onClick={() => navigate("/procurement/order-requests")}>
          Cancel
        </Button>
      }
    >
      {skippedOrders.length > 0 && (
        <Alert tone="warn" icon={<FileWarning aria-hidden />} title="Some orders were skipped">
          Only orders Waiting for approval or Approved can be grouped —{" "}
          {skippedOrders.map((o) => o.orCode).join(", ")}{" "}
          {skippedOrders.length === 1 ? "was" : "were"} not, so {skippedOrders.length === 1 ? "it wasn't" : "they weren't"} added.
        </Alert>
      )}

      <Card icon={<Layers aria-hidden />} title="Group Order Information">
        <FieldGrid>
          <Input
            label="Group Order Title"
            required
            placeholder="e.g. Q3 office supplies restock"
            value={form.title}
            error={showTitleError ? titleError : undefined}
            onChange={(e) => setField("title", e.target.value)}
          />
          <Select
            label="Region"
            required
            value={form.region}
            onChange={(v) => setField("region", v)}
            options={REGION_OPTIONS}
          />
          <Select
            label="Urgency"
            value={form.urgency}
            allowEmpty={false}
            onChange={(v) => setField("urgency", v)}
            options={URGENCY_OPTIONS}
          />
        </FieldGrid>
      </Card>

      <Card
        icon={<Layers aria-hidden />}
        title="Order Requests in this Group"
        subtitle={`${items.length} request${items.length === 1 ? "" : "s"} selected`}
      >
        <DataTable
          columns={columns}
          rows={items}
          empty={
            <EmptyState
              icon={<FileWarning aria-hidden />}
              title="No order requests selected"
              text="Go back to Order Request, tick the requests you want to group, then click “Create Group Order”."
              action={
                <Button variant="primary" onClick={() => navigate("/procurement/order-requests")}>
                  Back to Order Request
                </Button>
              }
            />
          }
        />
        {items.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              alignItems: "baseline",
              marginTop: 12,
              paddingTop: 12,
              borderTop: "1px solid var(--pc-border)",
            }}
          >
            <span className="pc-stat-label">Group total</span>
            <Currency value={totalAmount} strong />
          </div>
        )}
      </Card>

      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="primary"
          leftIcon={<Send aria-hidden />}
          disabled={!canSubmit}
          onClick={() => {
            setShowTitleError(true)
            if (!titleError) setConfirmOpen(true)
          }}
        >
          {busy ? "Creating…" : "Create Group Order"}
        </Button>
      </div>
      {confirmOpen && (
        <OrderSubmitConfirmModal
          title="Confirm Group Order"
          maxWidth={640}
          busy={busy}
          rows={[
            { label: "Group title", value: form.title },
            { label: "Region", value: form.region },
            { label: "Urgency", value: form.urgency },
            { label: "Order requests", value: items.length },
            { label: "Group total", value: <Currency value={totalAmount} strong /> },
            {
              label: "Included orders",
              full: true,
              value: (
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                  {items.map((r) => (
                    <li
                      key={r.id}
                      style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "block", fontWeight: 600 }}>
                          {r.orCode} — {r.title}
                        </span>
                        <span style={{ display: "block", fontSize: 12, color: "var(--pc-muted)" }}>{r.categoryName}</span>
                      </span>
                      <span style={{ flexShrink: 0, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                        <Currency value={r.total} />
                      </span>
                    </li>
                  ))}
                </ul>
              ),
            },
          ]}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleCreate}
        />
      )}
    </Page>
  )
}

export default GroupOrderFormContainer
